/**
 * The wording and the client-side rules for emailing several estimates at
 * once (#1349).
 *
 * React-free so it can be tested without mounting anything — the same reason
 * `invoice-batch/batch-charge-copy.ts` is. The server owns the real decisions
 * (it re-reads every estimate, re-derives every filename and re-checks every
 * status inside the send); this is what the office READS before committing,
 * and a screen that misdescribes what a send is about to do to a customer is
 * its own kind of bug.
 */

/** Mirrors the server's `BULK_ESTIMATE_MAX`. Refuses past it; never truncates. */
export const MAX_BULK_ESTIMATES = 25;

export interface EstimateLike {
    id: string;
    status: string;
}

/**
 * Which statuses a bulk send will carry.
 *
 * `accepted` is in here on Corbin's call (2026-08-31, "resend accepted ones
 * too"): a customer often wants the approved quote again for their own
 * records, and sending it is a plain copy that moves nothing.
 */
const SENDABLE = new Set(["draft", "sent", "accepted"]);

/** Can this row be checked? The server re-checks and is the authority. */
export function isSendable(r: EstimateLike): boolean {
    return SENDABLE.has(r.status);
}

/** Why the checkbox is disabled, in the office's words. */
export function unsendableReason(r: EstimateLike): string {
    if (r.status === "declined") return "Declined — reopen it first if this should go out again.";
    if (r.status === "cancelled") return "Cancelled — there's nothing to send.";
    return `Can't send an estimate that's ${r.status.replace(/_/g, " ")}.`;
}

/**
 * What the selection actually covers, in words, for the bar above the table.
 *
 * Never implies the selection reached rows still on the server: the office can
 * only check what is loaded, and a bar that says otherwise turns a partial
 * send into a silent one.
 */
export function selectionScopeLabel(loaded: number, total: number | undefined, sendable: number): string {
    const all = total === undefined || total <= loaded;
    const scope = all
        ? `All ${loaded} estimate${loaded === 1 ? "" : "s"} on this account are loaded`
        : `${loaded} of ${total} estimates are loaded`;
    return `${scope} — ${sendable} can be sent.`;
}

/** The select-all line: how many of the rows on screen can actually be picked. */
export function selectAllLabel(sendable: number): string {
    return `Select all ${sendable} sendable estimate${sendable === 1 ? "" : "s"} shown`;
}

/** The count line beside it, including why some rows are out of reach. */
export function selectionCountLabel(selected: number, blocked: number): string {
    const picked = selected === 0 ? "None selected" : `${selected} selected`;
    if (blocked === 0) return picked;
    return `${picked} · ${blocked} declined/cancelled can't be sent`;
}

/** Refusal for a selection the server would reject outright. Null = fine. */
export function validateSelection(count: number): string | null {
    if (count === 0) return "Pick at least one estimate.";
    if (count > MAX_BULK_ESTIMATES)
        return `${MAX_BULK_ESTIMATES} estimates is the most one email can carry — you've picked ${count}.`;
    return null;
}

/**
 * The send button's own label. It counts the RECIPIENTS, not the estimates,
 * because that is the number the office is about to be responsible for.
 */
export function sendButtonLabel(recipientCount: number): string {
    if (recipientCount === 0) return "Add someone to send to";
    return `Send to ${recipientCount} ${recipientCount === 1 ? "person" : "people"}`;
}

/**
 * The warning above the send when drafts are in the selection. Returns null
 * when there is nothing to warn about — a resend-only batch moves no status,
 * and a warning that fires every time stops being read.
 */
export function draftWarning(draftNumbers: string[], resendCount: number): string | null {
    if (draftNumbers.length === 0) return null;
    const list = draftNumbers.join(", ");
    const head =
        draftNumbers.length === 1
            ? `One of these is a draft. Sending marks ${list} as Sent and stamps the date, the same as emailing it on its own.`
            : `${draftNumbers.length} of these are drafts. Sending marks ${list} as Sent and stamps the date, the same as emailing them one at a time.`;
    if (resendCount === 0) return head;
    return `${head} The other ${resendCount === 1 ? "one is a resend — its status doesn't" : `${resendCount} are resends — their statuses don't`} move.`;
}

/** Human file size for the zip line. Deliberately coarse — it is a sanity check. */
export function fileSize(bytes: number): string {
    if (bytes <= 0) return "0 KB";
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * What actually happened, in counts and addresses.
 *
 * "Sent to 1 of 2" plus the address that bounced beats a green tick every
 * time: the office's next action depends on which address failed, and a
 * success toast hides that entirely.
 */
export function sendResultLine(sentTo: string[], failedTo: string[], estimateCount: number): string {
    const noun = `${estimateCount} estimate${estimateCount === 1 ? "" : "s"}`;
    if (sentTo.length === 0) {
        return `Couldn't email ${noun} — ${failedTo.length === 1 ? failedTo[0] : `none of the ${failedTo.length} addresses`} was rejected. Nothing was marked Sent.`;
    }
    if (failedTo.length === 0) {
        return `${noun} emailed to ${sentTo.length} ${sentTo.length === 1 ? "person" : "people"}.`;
    }
    const total = sentTo.length + failedTo.length;
    return `Sent to ${sentTo.length} of ${total}. ${failedTo.join(", ")} ${
        failedTo.length === 1 ? "was" : "were"
    } rejected — fix the address on the Contacts tab and send again. The ${noun} stay marked Sent.`;
}

/** The empty state: "nothing open" is never the same as "no estimates". */
export function emptySelectionMessage(totalRows: number, sendableRows: number): string {
    if (totalRows === 0) return "No estimates for this customer yet.";
    if (sendableRows === 0)
        return `Nothing to send. All ${totalRows} estimate${totalRows === 1 ? " is" : "s are"} declined or cancelled — reopen one to email it again.`;
    return "Pick the estimates you want to email.";
}
